import React, { useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth'
import { TextField, Button, Typography, Box, Stack, Alert, CircularProgress } from '@mui/material'
import { firebase_auth } from '../../../config/firebaseConfig'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'

interface Props {
  continueUrl?: string
}

const SetPasswordForm: React.FC<Props> = ({ continueUrl }) => {
  const [email, setEmail] = useState<string>('')
  const [newPassword, setNewPassword] = useState<string>('')
  const [confirmPassword, setConfirmPassword] = useState<string>('')
  const [success, setSuccess] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  function getParameterByName(name: string) {
    const urlSearchParams = new URLSearchParams(window.location.search)
    return urlSearchParams.get(name)
  }

  useEffect(() => {
    const actionCode = getParameterByName('oobCode')
    if (!actionCode) {
      setError('Invalid or missing reset code. Please request a new password reset link.')
      setLoading(false)
      return
    }
    verifyPasswordResetCode(firebase_auth, actionCode)
      .then((accountEmail) => {
        setEmail(accountEmail)
        setLoading(false)
      })
      .catch(() => {
        setError('This password reset link has expired or is invalid. Please request a new one.')
        setLoading(false)
      })
  }, [])

  const handleResetPassword = () => {
    setError(null)

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long.')
      return
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    const actionCode = getParameterByName('oobCode')
    setSubmitting(true)

    confirmPasswordReset(firebase_auth, actionCode || '', newPassword)
      .then(() => {
        setSuccess(true)
        setSubmitting(false)
      })
      .catch((err) => {
        setSubmitting(false)
        if (err.code === 'auth/weak-password') {
          setError('Password is too weak. Please choose a stronger password.')
        } else if (err.code === 'auth/expired-action-code') {
          setError('This reset link has expired. Please request a new one.')
        } else {
          setError('Failed to reset password. Please try again.')
        }
      })
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (success) {
    return (
      <Stack spacing={3} sx={{ textAlign: 'center', px: 4, py: 2 }}>
        <Box>
          <Typography sx={{ fontSize: '22px', fontWeight: 500 }}>Password Updated</Typography>
          <Typography sx={{ fontSize: '18px', color: 'gray', mt: 1 }}>
            You can now login with your new password
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CheckCircleOutlineIcon sx={{ color: '#1DC30E', fontSize: '80px' }} />
        </Box>
        <Button variant="contained" component={RouterLink} to="/login" sx={{ color: 'white' }}>
          Go to Login
        </Button>
      </Stack>
    )
  }

  if (error && !email) {
    return (
      <Stack spacing={3} sx={{ textAlign: 'center', px: 4, py: 2 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          variant="contained"
          component={RouterLink}
          to="/forgot-password"
          sx={{ color: 'white' }}
        >
          Request New Reset Link
        </Button>
      </Stack>
    )
  }

  return (
    <Stack spacing={3} sx={{ textAlign: 'center', px: 4, py: 2 }}>
      <Box>
        <Typography sx={{ fontSize: '22px', fontWeight: 500 }}>Set New Password</Typography>
        <Typography sx={{ fontSize: '18px', color: 'gray', mt: 1 }}>
          Enter a new password for {email}
        </Typography>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      <TextField
        fullWidth
        label="New Password"
        type="password"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        disabled={submitting}
      />

      <TextField
        fullWidth
        label="Confirm Password"
        type="password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        disabled={submitting}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleResetPassword()
          }
        }}
      />

      <Button
        variant="contained"
        onClick={handleResetPassword}
        disabled={submitting}
        sx={{ color: 'white' }}
      >
        {submitting ? 'Setting Password...' : 'Set Password'}
      </Button>

      <Button component={RouterLink} to="/login">
        Back to Login
      </Button>
    </Stack>
  )
}

export default SetPasswordForm
